import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import {
  Bold,
  Italic,
  Heading2,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Quote,
  Undo2,
  Redo2,
} from 'lucide-react';
import type { TemplateVariable } from '@/services/emailCrmService';

interface TemplateEditorProps {
  value: string;
  onChange: (html: string) => void;
  variables: TemplateVariable[];
}

/**
 * Convert backend `{{key}}` placeholders into a Tiptap mention node so they
 * render as removable chips in the editor. Inverse of `serialize`.
 */
const deserialize = (html: string, variables: TemplateVariable[]): string => {
  const labelByKey = new Map(variables.map((v) => [v.key, v.label]));
  return html.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_m, raw: string) => {
    const key = raw.trim();
    const label = labelByKey.get(key) ?? key;
    return `<span data-type="mention" data-id="${key}" data-label="${label}"></span>`;
  });
};

/**
 * Convert Tiptap mention nodes back into `{{key}}` so the saved HTML is
 * directly renderable by the backend.
 */
const serialize = (html: string): string => {
  return html.replace(
    /<span[^>]*data-type="mention"[^>]*data-id="([^"]+)"[^>]*>(?:[^<]*)<\/span>/g,
    (_m, id: string) => `{{${id}}}`,
  );
};

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  value,
  onChange,
  variables,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({
        placeholder:
          "Bonjour {{firstName}},\n\nÉcrivez votre message ici. Glissez les variables depuis le panneau de droite pour les insérer.",
      }),
      Mention.configure({
        HTMLAttributes: {
          class:
            'mention inline-flex items-center px-2 py-0.5 mx-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-800 align-baseline',
        },
        renderHTML: ({ node }) => {
          const id = node.attrs.id as string;
          const label = (node.attrs.label as string) ?? id;
          return [
            'span',
            {
              'data-type': 'mention',
              'data-id': id,
              'data-label': label,
              class:
                'mention inline-flex items-center px-2 py-0.5 mx-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-800 align-baseline',
            },
            `{{${label}}}`,
          ];
        },
      }),
    ],
    content: deserialize(value || '', variables),
    onUpdate: ({ editor: e }) => {
      onChange(serialize(e.getHTML()));
    },
  });

  // If `value` is set externally (e.g. picking a template), reset editor content
  useEffect(() => {
    if (!editor) return;
    const current = serialize(editor.getHTML());
    if (current !== value) {
      editor.commands.setContent(deserialize(value || '', variables), { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const insertVariable = (v: TemplateVariable) => {
    editor.chain().focus().insertContent({
      type: 'mention',
      attrs: { id: v.key, label: v.label },
    }).insertContent(' ').run();
  };

  const buttonClass = (active: boolean) =>
    `p-2 rounded hover:bg-gray-100 ${active ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600'}`;

  const setLink = () => {
    const url = window.prompt("URL du lien (laisser vide pour retirer)");
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const setImage = () => {
    const url = window.prompt("URL de l'image");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const lead = variables.filter((v) => v.group === 'lead');
  const custom = variables.filter((v) => v.group === 'custom');
  const system = variables.filter((v) => v.group === 'system');

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Editor */}
      <div className="col-span-9 border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-1 px-2 py-1 border-b border-gray-200 bg-gray-50">
          <button
            type="button"
            className={buttonClass(editor.isActive('bold'))}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Gras"
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            className={buttonClass(editor.isActive('italic'))}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Italique"
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            className={buttonClass(editor.isActive('heading', { level: 2 }))}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title="Titre"
          >
            <Heading2 size={16} />
          </button>
          <span className="w-px h-5 bg-gray-300 mx-1" />
          <button
            type="button"
            className={buttonClass(editor.isActive('bulletList'))}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Liste à puces"
          >
            <List size={16} />
          </button>
          <button
            type="button"
            className={buttonClass(editor.isActive('orderedList'))}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Liste numérotée"
          >
            <ListOrdered size={16} />
          </button>
          <button
            type="button"
            className={buttonClass(editor.isActive('blockquote'))}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="Citation"
          >
            <Quote size={16} />
          </button>
          <span className="w-px h-5 bg-gray-300 mx-1" />
          <button
            type="button"
            className={buttonClass(editor.isActive('link'))}
            onClick={setLink}
            title="Insérer un lien"
          >
            <LinkIcon size={16} />
          </button>
          <button
            type="button"
            className={buttonClass(false)}
            onClick={setImage}
            title="Insérer une image"
          >
            <ImageIcon size={16} />
          </button>
          <span className="w-px h-5 bg-gray-300 mx-1" />
          <button
            type="button"
            className={buttonClass(false)}
            onClick={() => editor.chain().focus().undo().run()}
            title="Annuler"
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            className={buttonClass(false)}
            onClick={() => editor.chain().focus().redo().run()}
            title="Rétablir"
          >
            <Redo2 size={16} />
          </button>
        </div>
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none [&_.ProseMirror]:min-h-[280px] [&_.ProseMirror]:outline-none"
        />
      </div>

      {/* Variable picker */}
      <aside className="col-span-3">
        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Variables
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Cliquez sur une variable pour l'insérer dans l'email.
          </p>

          {lead.length > 0 && <VariableGroup title="Champs lead" items={lead} onPick={insertVariable} />}
          {custom.length > 0 && (
            <VariableGroup title="Champs personnalisés" items={custom} onPick={insertVariable} />
          )}
          {system.length > 0 && (
            <VariableGroup title="Système" items={system} onPick={insertVariable} />
          )}
        </div>
      </aside>
    </div>
  );
};

const VariableGroup: React.FC<{
  title: string;
  items: TemplateVariable[];
  onPick: (v: TemplateVariable) => void;
}> = ({ title, items, onPick }) => (
  <div className="mb-3 last:mb-0">
    <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">{title}</div>
    <div className="flex flex-wrap gap-1.5">
      {items.map((v) => (
        <button
          key={v.key}
          type="button"
          onClick={() => onPick(v)}
          className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 border border-indigo-100"
          title={v.key}
        >
          {v.label}
        </button>
      ))}
    </div>
  </div>
);
